import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  Pager,
  Section,
  TableScroller,
  ToolbarInput,
  ToolbarSelect,
  toast,
  useBearer,
  useDebouncedValue,
  usePagedQuery,
  useToast,
} from './_admin-utils';

interface AllowlistItem {
  id?: string;
  email: string;
  name?: string;
  role: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface CsvRow {
  _row: number;
  email: string;
  csvRole: string;
  name: string;
  active: boolean;
  role?: string;
}

interface ValidationError {
  row: number;
  message: string;
}

interface ImportMessage {
  text: string;
  tone: 'success' | 'error';
}

const ROLE_OPTIONS = [
  { value: 'teacher', label: 'Teacher' },
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'coach', label: 'Coach' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'admin', label: 'Admin' },
  { value: 'superadmin', label: 'Superadmin' },
];

const ROLE_VALUES = ROLE_OPTIONS.map((option) => option.value);

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

async function sendMagicLink(email: string): Promise<void> {
  const target = (email || '').trim().toLowerCase();
  if (!target) throw new Error('Email required');
  const CORE_API = import.meta.env.VITE_CORE_API_URL || '';
  const res = await fetch(`${CORE_API}/api/v1/auth/request-link`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email: target }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  toast(`Sent magic link to ${target}`);
}

export default function Allowlist() {
  const { profile } = useAuth();
  const bearer = useBearer();
  const { show, ToastHost } = useToast();

  const [role, setRole] = useState<string>('teacher');
  const [query, setQuery] = useState<string>('');
  const [bulk, setBulk] = useState<string>('');
  const [bulkActive, setBulkActive] = useState<boolean>(true);
  const [savingBulk, setSavingBulk] = useState<boolean>(false);
  const [togglingKey, setTogglingKey] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(query.trim(), 300);

  const fetcher = async ({ page, pageSize }: { page: number; pageSize: number }) => {
    if (!bearer) throw new Error('Not authenticated');
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    if (debouncedQuery) params.set('q', debouncedQuery);
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));

    const response = await fetch(`/api/v1/admin/allowlist?${params.toString()}`, {
      headers: { ...bearer },
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json?.error?.message || json?.error || 'Failed to load allowlist');
    }
    const d = json?.data || json;
    return {
      items: Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : [],
      total: typeof d?.total === 'number' ? d.total : 0,
    };
  };

  const { items, page, pageCount, total, loading, error, load } = usePagedQuery<AllowlistItem>(fetcher, [
    role,
    debouncedQuery,
  ]);

  const handleToggle = useCallback(
    async (item: AllowlistItem) => {
      if (!bearer) return;
      const key = `${item.email}:${item.role}`;
      setTogglingKey(key);
      try {
        const response = await fetch('/api/v1/admin/allowlist/toggle', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            ...bearer,
          },
          body: JSON.stringify({ email: item.email, role: item.role, active: !item.active }),
        });
        const json = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(json?.error || `HTTP ${response.status}`);
        show(item.active ? 'Entry disabled' : 'Entry activated');
        await load(page);
      } catch (err) {
        alert((err as Error)?.message || 'Unable to update entry');
      } finally {
        setTogglingKey(null);
      }
    },
    [bearer, load, page, show],
  );

  const handleBulkSubmit = useCallback(async () => {
    if (!bearer) return;
    if (!bulk.trim()) return;
    const lines = bulk
      .split(/\r?\n/)
      .map((line) => line.trim().toLowerCase())
      .filter(Boolean);
    if (!lines.length) {
      alert('No valid emails');
      return;
    }
    const rows = lines.map((email) => ({ email, active: bulkActive, role }));
    setSavingBulk(true);
    try {
      const res = await fetch('/api/v1/admin/allowlist/bulk', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...bearer,
        },
        body: JSON.stringify({ role, rows, source: 'textarea' }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      show(`Imported ${json.count} entries`);
      setBulk('');
      await load(1);
    } catch (e) {
      alert((e as Error).message || 'Bulk add failed');
    } finally {
      setSavingBulk(false);
    }
  }, [bearer, bulk, bulkActive, load, role, show]);

  const isAdmin = useMemo(() => ['admin', 'superadmin'].includes(profile?.role ?? ''), [profile?.role]);
  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-6">
      <ToastHost />
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Allowlist</h1>
        <p className="text-sm text-cocoa/70">Manage email allowlist entries for invitations and login flows.</p>
      </header>

      <Section
        title="Entries"
        actions={
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)]">
            <ToolbarSelect value={role} onChange={setRole} options={ROLE_OPTIONS} />
            <ToolbarInput value={query} onChange={setQuery} placeholder="Search email" />
          </div>
        }
      >
        {error ? (
          <div className="rounded-md border border-brick/30 bg-brick/10 px-3 py-2 text-sm text-brick">{error}</div>
        ) : null}

        <div className="overflow-hidden rounded-xl border border-white/70 bg-white/60">
          <TableScroller minWidth={760}>
            <table className="min-w-full divide-y divide-white/70 text-sm">
              <thead className="bg-shell/60 text-xs uppercase tracking-wide text-cocoa/70">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Created</th>
                  <th className="px-4 py-3 font-semibold">Updated</th>
                  <th className="px-4 py-3 font-semibold">Test</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/60 bg-white text-cocoa">
                {items.map((item) => {
                  const key = item.id || `${item.email}:${item.role}`;
                  return (
                    <tr key={key} className="align-top">
                      <td className="px-4 py-3 font-medium text-cocoa break-all">{item.email}</td>
                      <td className="px-4 py-3 text-cocoa/70 text-sm">{item.name || '—'}</td>
                      <td className="px-4 py-3 text-cocoa/80 uppercase text-xs tracking-wide">{item.role}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                            item.active ? 'bg-mint/20 text-mint' : 'bg-brick/10 text-brick'
                          }`}
                        >
                          {item.active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-cocoa/70 text-xs">{formatDate(item.created_at)}</td>
                      <td className="px-4 py-3 text-cocoa/70 text-xs">{formatDate(item.updated_at)}</td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          className="text-xs px-2 py-1 border border-white/70 rounded bg-white hover:bg-shell/30"
                          onClick={async () => {
                            try {
                              await sendMagicLink(item.email);
                            } catch (err) {
                              alert((err as Error)?.message || 'Failed to send magic link');
                            }
                          }}
                          title="Send test magic link"
                        >
                          Test link
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleToggle(item)}
                          disabled={togglingKey === `${item.email}:${item.role}`}
                          className="rounded-full border border-white/70 bg-shell/40 px-3 py-1 text-xs font-semibold uppercase tracking-wide hover:bg-shell/60 disabled:opacity-50"
                        >
                          {item.active ? 'Disable' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!items.length && !loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-sm text-cocoa/60">
                      No entries found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </TableScroller>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="text-xs text-cocoa/60">Total: {total}</div>
          <Pager page={page} pageCount={pageCount} onPage={(next) => !loading && load(next)} />
        </div>
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-3 border border-white/70 rounded-lg bg-white shadow-sm">
          <div className="mb-2 space-y-1">
            <div className="text-sm font-medium text-cocoa">Import CSV</div>
            <p className="text-xs text-cocoa/70">Preview a CSV and import allowlist entries.</p>
          </div>
          <Link
            to="/admin/allowlist/import"
            className="inline-flex items-center rounded-full border border-terracotta px-4 py-1.5 text-sm font-semibold text-terracotta transition hover:bg-terracotta/10"
          >
            Open Import Page
          </Link>
        </div>
        <div className="p-3 border border-white/70 rounded-lg bg-white/60 text-sm text-cocoa/70 space-y-1">
          <div className="font-semibold text-cocoa">Tips</div>
          <p>Entries inherit the selected role unless overridden in CSV uploads.</p>
          <p>Use the test buttons to confirm Supabase auth can send magic links.</p>
        </div>
      </div>

      <Section
        title="Bulk add via textarea"
        actions={<span className="text-xs text-cocoa/60">Paste one email per line.</span>}
      >
        <div className="space-y-3">
          <textarea
            rows={6}
            value={bulk}
            onChange={(event) => setBulk(event.target.value)}
            className="w-full rounded-lg border border-white/70 bg-white/70 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
            placeholder={'alice@example.org\nbob@example.org'}
          />
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <label className="flex items-center gap-2 text-sm text-cocoa/80">
              <input
                type="checkbox"
                checked={bulkActive}
                onChange={(event) => setBulkActive(event.target.checked)}
              />
              Active immediately
            </label>
            <div className="flex items-center text-sm text-cocoa/80">
              <span className="mr-2">Role:</span>
              <ToolbarSelect value={role} onChange={setRole} options={ROLE_OPTIONS} />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 text-xs font-semibold uppercase tracking-wide border border-white/70 rounded bg-white hover:bg-shell/40"
                onClick={async () => {
                  const first = (bulk || '')
                    .split(/\r?\n/)
                    .map((line) => line.trim())
                    .filter(Boolean)[0];
                  if (!first) {
                    alert('Nothing to test');
                    return;
                  }
                  try {
                    await sendMagicLink(first);
                  } catch (err) {
                    alert((err as Error)?.message || 'Failed to send magic link');
                  }
                }}
              >
                Test first email
              </button>
              <button
                type="button"
                onClick={handleBulkSubmit}
                disabled={savingBulk || !bulk.trim()}
                className="rounded-full border border-white/70 bg-shell/40 px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:bg-shell/60 disabled:opacity-50"
              >
                {savingBulk ? 'Saving…' : 'Save entries'}
              </button>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

const parseActive = (value: string | boolean | null | undefined): boolean => {
  if (value === undefined || value === null || value === '') return true;
  const normalized = String(value).trim().toLowerCase();
  return ['true', '1', 'yes', 'y'].includes(normalized);
};

export function AllowlistImport() {
  const { profile } = useAuth();
  const bearer = useBearer();

  const [role, setRole] = useState<string>('teacher');
  const [rawCsv, setRawCsv] = useState<string>('');
  const [rawRows, setRawRows] = useState<CsvRow[]>([]);
  const [displayRows, setDisplayRows] = useState<CsvRow[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [message, setMessage] = useState<ImportMessage | null>(null);

  const isAdmin = useMemo(() => ['admin', 'superadmin'].includes(profile?.role ?? ''), [profile?.role]);
  const previewRows = useMemo(() => displayRows.slice(0, 50), [displayRows]);
  const totalRows = displayRows.length;

  const refreshRows = useCallback((rows: CsvRow[], selectedRole: string) => {
    if (!rows.length) {
      setDisplayRows([]);
      setErrors([]);
      return;
    }

    const computed = rows.map((row) => {
      const baseRole = row.csvRole ? row.csvRole.toLowerCase() : '';
      const resolvedRole = (baseRole || selectedRole || 'teacher').toLowerCase();
      return {
        ...row,
        role: resolvedRole,
      };
    });

    const nextErrors: ValidationError[] = [];
    computed.forEach((row) => {
      if (!row.email || !EMAIL_RX.test(row.email)) {
        nextErrors.push({ row: row._row, message: 'invalid email' });
      }
      if (!ROLE_VALUES.includes(row.role)) {
        nextErrors.push({ row: row._row, message: 'invalid role' });
      }
    });

    setDisplayRows(computed);
    setErrors(nextErrors);
  }, []);

  const parseText = useCallback(
    (text: string, selectedRole: string) => {
      const filtered = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);

      if (!filtered.length) {
        setRawRows([]);
        setDisplayRows([]);
        setErrors([{ row: 1, message: 'no rows found' }]);
        setPreviewOpen(false);
        return;
      }

      const header = filtered.shift();
      if (!header) {
        setRawRows([]);
        setDisplayRows([]);
        setErrors([{ row: 1, message: 'missing header row' }]);
        setPreviewOpen(false);
        return;
      }

      const heads = header.split(',').map((token) => token.trim().toLowerCase());
      const emailIndex = heads.indexOf('email');
      if (emailIndex === -1) {
        setRawRows([]);
        setDisplayRows([]);
        setErrors([{ row: 1, message: 'missing email column' }]);
        setPreviewOpen(false);
        return;
      }

      const roleIndex = heads.indexOf('role');
      const nameIndex = heads.indexOf('name');
      const activeIndex = heads.indexOf('active');

      const parsed = filtered.map((line, index) => {
        const cols = line.split(',').map((token) => token.trim());
        const email = (cols[emailIndex] || '').toLowerCase();
        const csvRole = roleIndex >= 0 ? cols[roleIndex] || '' : '';
        const name = nameIndex >= 0 ? cols[nameIndex] || '' : '';
        const activeValue = activeIndex >= 0 ? cols[activeIndex] : undefined;
        return {
          _row: index + 2,
          email,
          csvRole,
          name,
          active: parseActive(activeValue),
        };
      });

      setRawRows(parsed);
      setPreviewOpen(true);
    },
    [refreshRows],
  );

  const onFile = useCallback(
    async (file: File | null) => {
      if (!file) return;
      setBusy(true);
      try {
        const text = await file.text();
        setRawCsv(text);
        setMessage(null);
        parseText(text, role);
      } catch (err) {
        console.error('[allowlist-import] failed to read file', err);
        setMessage({ text: 'Unable to read file', tone: 'error' });
      } finally {
        setBusy(false);
      }
    },
    [parseText, role],
  );

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const file = event.dataTransfer?.files?.[0];
      if (file) onFile(file);
    },
    [onFile],
  );

  const importCsv = async () => {
    if (!bearer) return;
    if (!rawCsv) {
      setMessage({ text: 'Upload a CSV file first.', tone: 'error' });
      return;
    }
    if (errors.length) {
      setMessage({ text: 'Resolve validation errors before importing.', tone: 'error' });
      return;
    }
    if (!displayRows.length) {
      setMessage({ text: 'No rows to import.', tone: 'error' });
      return;
    }

    setBusy(true);
    try {
      const rows = displayRows.map((row) => ({
        email: row.email,
        role: row.role,
        name: row.name,
        active: row.active,
      }));
      const response = await fetch('/api/v1/admin/allowlist/bulk', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...bearer,
        },
        body: JSON.stringify({ role, rows, source: 'csv' }),
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || `HTTP ${response.status}`);

      setMessage({ text: `Imported ${json.count || rows.length}`, tone: 'success' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ text: (error as Error)?.message || 'Import failed', tone: 'error' });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!rawRows.length) return;
    refreshRows(rawRows, role);
  }, [rawRows, role, refreshRows]);

  if (!isAdmin) {
    return <div className="p-6 text-sm text-cocoa/70">You do not have permission to view this page.</div>;
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 px-4 py-8">
      <div>
        <Link to="/admin/allowlist" className="text-sm font-medium text-terracotta hover:underline">
          ← Back to Allowlist
        </Link>
      </div>

      <Section
        title="Allowlist — Import CSV"
        actions={
          <select
            value={role}
            onChange={(event) => setRole(event.target.value)}
            className="rounded border border-white/70 bg-white px-2 py-1 text-sm text-cocoa"
          >
            {ROLE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        }
      >
        <div
          onDrop={onDrop}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          className="flex flex-col items-center justify-center rounded border-2 border-dashed border-shell bg-white p-8 text-center"
        >
          <div className="mb-2 text-sm font-medium text-cocoa">Drag &amp; drop CSV here</div>
          <div className="mb-3 text-xs text-cocoa/60">or choose a file</div>
          <input
            type="file"
            accept=".csv,text/csv"
            disabled={busy}
            onChange={(event) => onFile(event.target.files?.[0] || null)}
          />
          <p className="mt-3 text-xs text-cocoa/60">
            Columns: email, role (optional), name (optional), active (optional)
          </p>
        </div>

        {message ? (
          <div
            className={`mt-3 rounded border px-3 py-2 text-sm ${
              message.tone === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-brick/50 bg-brick/10 text-brick'
            }`}
          >
            {message.text}
          </div>
        ) : null}

        {previewOpen && (
          <div className="mt-5 space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-sm font-semibold text-cocoa">
                Preview (showing {previewRows.length} of {totalRows} rows)
              </h3>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded-full border border-white/70 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-cocoa hover:bg-shell/40"
                  onClick={async () => {
                    try {
                      const samples = displayRows.slice(0, 3);
                      if (!samples.length) {
                        alert('No rows in preview');
                        return;
                      }
                      for (const sample of samples) {
                        const email = (sample.email || '').trim();
                        if (!email) continue;
                        await sendMagicLink(email);
                      }
                    } catch (err) {
                      alert((err as Error)?.message || 'Failed to send sample links');
                    }
                  }}
                  disabled={busy || !displayRows.length}
                >
                  Test links (sample)
                </button>
                <button
                  type="button"
                  onClick={importCsv}
                  disabled={busy || !displayRows.length || errors.length > 0}
                  className="rounded-full bg-terracotta px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-terracotta/80 disabled:opacity-60"
                >
                  {busy ? 'Importing…' : 'Import'}
                </button>
              </div>
            </div>

            {errors.length > 0 ? (
              <div className="rounded border border-brick/40 bg-brick/10 p-3 text-sm text-brick">
                <div className="font-semibold">Fix these rows before importing:</div>
                <div className="mt-2 space-y-1">
                  {errors.slice(0, 10).map((error, index) => (
                    <div key={`${error.row}-${index}`}>Row {error.row}: {error.message}</div>
                  ))}
                  {errors.length > 10 ? <div>…and {errors.length - 10} more</div> : null}
                </div>
              </div>
            ) : null}

            <div className="max-h-96 overflow-auto rounded border border-white/70">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-shell/60 text-xs uppercase tracking-wide text-cocoa/70">
                  <tr className="text-left">
                    <th className="px-3 py-2">Row</th>
                    <th className="px-3 py-2">Email</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2">Name</th>
                    <th className="px-3 py-2">Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/70 bg-white text-cocoa">
                  {previewRows.map((row, index) => (
                    <tr key={`${row._row}-${index}`} className="align-top">
                      <td className="px-3 py-2 text-xs text-cocoa/60">{row._row}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.email || '—'}</td>
                      <td className="px-3 py-2 text-xs uppercase tracking-wide text-cocoa/70">{row.role || '—'}</td>
                      <td className="px-3 py-2 text-xs text-cocoa/70">{row.name || '—'}</td>
                      <td className="px-3 py-2 text-xs text-cocoa/70">{row.active ? 'true' : 'false'}</td>
                    </tr>
                  ))}
                  {!previewRows.length ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-6 text-center text-sm text-cocoa/60">
                        No rows to preview.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
